import React, { useState, useEffect } from "react";
import { Box, Switch, Typography, TextField, Button, Alert, MenuItem, Chip, Stack, Tooltip, IconButton, CircularProgress } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useNavigate } from "react-router-dom";
import { getConfig, saveConfig } from "../api/agentApi";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";
import ConfigSectionToggle from "./ConfigSectionToggle";
import { useFeatureFlags } from "../context/FeatureFlagContext";
import { useUser } from "./UserContext";
import { PERMISSIONS } from "../permissions";

const DEFAULT_NEEDLEPATH_CONFIG = {
  provider: "embedded",
  enabled: false,
  mode: "shadow",
  canary_percentage: 0,
  input_optimization_enabled: true,
  output_contract_enabled: true,
  metrics_enabled: true,
  safety_profile: "conservative",
  context_format: "hybrid",
  output_mode: "auto",
  max_selected_tokens: 8000,
  max_selected_records: 40,
  timeout_ms: 500,
};

const NEEDLEPATH_COLORS = {
  copper: BRAND_COLORS.primary.main,
  copperDark: BRAND_COLORS.primary.dark,
  muted: "#6F6A65",
  soft: BRAND_COLORS.primary.softSubtle,
  blueSoft: "#EAF6FB",
};

const CONFIG_CARD_EFFECT = {
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: "0 2px 8px -2px rgba(0,0,0,0.1), 0 6px 12px -2px rgba(0,0,0,0.06)",
  "&:hover": {
    borderColor: NEEDLEPATH_COLORS.copper,
    boxShadow: "0 4px 12px -2px rgba(0,0,0,0.12), 0 12px 24px -4px rgba(0,0,0,0.08)",
    transform: "translateY(-2px)",
  },
};

const CONFIG_ACTION_BUTTON_SX = {
  minHeight: 44,
  borderRadius: UI_RADIUS.button,
  px: 2.35,
  bgcolor: "primary.main",
  color: "primary.contrastText",
  textTransform: "none",
  fontWeight: 850,
  letterSpacing: 0,
  boxShadow: "none",
  transition: "transform 160ms ease, background-color 160ms ease, box-shadow 160ms ease",
  "& .MuiButton-startIcon": {
    color: "inherit",
    mr: 1,
  },
  "&:hover": {
    bgcolor: "primary.dark",
    boxShadow: (theme) => `0 10px 22px -12px ${theme.palette.primary.main}`,
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    bgcolor: "action.disabledBackground",
    color: "action.disabled",
    boxShadow: "none",
    transform: "none",
  },
};

const NEEDLEPATH_HELP = {
  enabled: {
    helper: "Master switch. Keep Shadow mode for metrics-only rollout.",
    detail: "When off, Needlepath settings are saved but inactive. Turn this on, keep Rollout mode set to Shadow, then save to observe metrics without changing prompts or outputs.",
  },
  mode: {
    helper: "Shadow observes. Canary samples. On applies everywhere.",
    detail: "Shadow records telemetry only. Canary applies optimization to the configured percentage of traffic. On applies it to all eligible traffic. Off disables rollout even if the master switch is on.",
  },
  safety_profile: {
    helper: "How cautious Needlepath is before applying compression.",
    detail: "Conservative keeps more context and fails open more often. Balanced is the normal production default. Aggressive pursues more savings and should be rolled out only after shadow/canary validation.",
  },
  canary_percentage: {
    helper: "Only used when Rollout mode is Canary.",
    detail: "Set the percentage of eligible runs that should receive active optimization while the rest follow the normal path. Use 0 for no canary traffic and 100 for all eligible traffic.",
  },
  context_format: {
    helper: "How selected context is packed for the model.",
    detail: "Hybrid keeps natural-language grounding while adding compact structure. Plain favors readability. TOON is denser and may save more tokens for structured records.",
  },
  output_mode: {
    helper: "Controls response contract compactness.",
    detail: "Auto lets Needlepath choose. Normal preserves standard output. Concise, Dense, and Ultra progressively tighten output contracts. Off disables output contracts while leaving input optimization available.",
  },
  timeout_ms: {
    helper: "Latency budget before Needlepath fails open.",
    detail: "If Needlepath cannot finish selection or contract planning within this budget, the request continues through the normal agent path.",
  },
  max_selected_tokens: {
    helper: "Upper bound for retained context after selection.",
    detail: "This caps selected context tokens. Higher values preserve more grounding; lower values increase savings but can reduce evidence available to the model.",
  },
  max_selected_records: {
    helper: "Upper bound for retained evidence records.",
    detail: "This caps how many records can be selected from memory, retrieval, or intermediate state before token packing.",
  },
  input_optimization_enabled: {
    helper: "Selects and repacks context before the model call.",
    detail: "When enabled, Needlepath can reduce input tokens by choosing the most relevant records and formatting them compactly.",
  },
  output_contract_enabled: {
    helper: "Applies concise response contracts when active.",
    detail: "When enabled, Needlepath can shape model instructions so responses use fewer output tokens while preserving required fields and evidence.",
  },
  metrics_enabled: {
    helper: "Records savings, latency, and fail-open reasons.",
    detail: "Leave telemetry on during shadow and canary rollout so the dashboard can show savings, quality guardrails, and fallback behavior.",
  },
};

const ROLLOUT_OPTIONS = [
  {
    value: "shadow",
    eyebrow: "Shadow",
    title: "Observe",
    body: "Records telemetry only. Prompts and responses stay unchanged.",
  },
  {
    value: "canary",
    eyebrow: "Canary",
    title: "Pilot",
    body: "Applies optimization to a controlled slice of eligible runs.",
  },
  {
    value: "on",
    eyebrow: "On",
    title: "Apply",
    body: "Optimizes every eligible runtime path.",
  },
  {
    value: "off",
    eyebrow: "Off",
    title: "Paused",
    body: "Keeps settings saved but disables rollout.",
  },
];

function NeedlepathHelp({ help }) {
  if (!help) return null;
  return (
    <Tooltip title={help.detail} arrow placement="top">
      <IconButton
        size="small"
        aria-label="Needlepath setting help"
        sx={{ ml: 0.5, p: 0.25, color: "text.secondary" }}
      >
        <HelpOutlineIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}

function RolloutOption({ option, active, onSelect }) {
  const colors = NEEDLEPATH_COLORS;
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onSelect(option.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(option.value);
        }
      }}
      sx={{
        minHeight: 104,
        p: 1.5,
        borderRadius: UI_RADIUS.card,
        cursor: "pointer",
        border: active ? "1.5px solid" : "1px solid",
        borderColor: active ? colors.copper : "divider",
        bgcolor: (theme) => active
          ? (theme.palette.mode === "dark" ? BRAND_COLORS.primary.softSubtle : "#FFF8F0")
          : "background.paper",
        boxShadow: active
          ? "0 4px 12px -2px rgba(147,92,41,0.18), 0 12px 24px -4px rgba(147,92,41,0.12)"
          : CONFIG_CARD_EFFECT.boxShadow,
        transition: CONFIG_CARD_EFFECT.transition,
        outline: "none",
        "&:hover": {
          borderColor: colors.copper,
          bgcolor: (theme) => active
            ? (theme.palette.mode === "dark" ? BRAND_COLORS.primary.softSubtle : "#FFF8F0")
            : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#FFFCF8"),
          boxShadow: CONFIG_CARD_EFFECT["&:hover"].boxShadow,
          transform: CONFIG_CARD_EFFECT["&:hover"].transform,
        },
        "&:focus-visible": {
          boxShadow: `0 0 0 3px rgba(184,115,51,0.22)`,
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Chip
          size="small"
          label={option.eyebrow}
          sx={{
            height: 24,
            bgcolor: active ? "rgba(184,115,51,0.14)" : "#EFEAE5",
            color: active ? colors.copperDark : colors.muted,
            fontWeight: 800,
          }}
        />
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            border: active ? `5px solid ${colors.copper}` : "1.5px solid #CFC7BF",
            bgcolor: "background.paper",
            flexShrink: 0,
          }}
        />
      </Stack>
      <Typography sx={{ color: "text.primary", fontWeight: 850, fontSize: 20, lineHeight: 1.15 }}>
        {option.title}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, lineHeight: 1.4 }}>
        {option.body}
      </Typography>
    </Box>
  );
}

function ScopeToggle({ title, helper, checked, onChange, help }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        border: "1px solid",
        borderColor: checked ? BRAND_COLORS.primary.border : "divider",
        borderRadius: UI_RADIUS.card,
        bgcolor: (theme) => checked
          ? (theme.palette.mode === "dark" ? BRAND_COLORS.primary.softSubtle : "#FFF8F0")
          : "background.paper",
        ...CONFIG_CARD_EFFECT,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography sx={{ color: "text.primary", fontWeight: 850 }}>{title}</Typography>
          <NeedlepathHelp help={help} />
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
          {helper}
        </Typography>
      </Box>
      <Switch checked={checked} onChange={onChange} />
    </Box>
  );
}

function SettingsSection({ title, description, action, children }) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: UI_RADIUS.panel,
        bgcolor: "background.paper",
        overflow: "hidden",
        ...CONFIG_CARD_EFFECT,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "flex-start" }}
        spacing={2}
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 2,
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 900, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, maxWidth: 760 }}>
              {description}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      <Box sx={{ p: { xs: 2, md: 2.5 } }}>{children}</Box>
    </Box>
  );
}

function SettingRow({ title, description, checked, onChange, disabled = false }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 1.35,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ color: "text.primary", fontWeight: 850 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
          {description}
        </Typography>
      </Box>
      <Switch checked={checked} onChange={onChange} disabled={disabled} />
    </Box>
  );
}

function NeedlepathStatus({ needlepath }) {
  const colors = NEEDLEPATH_COLORS;
  const message = !needlepath.enabled
    ? "Disabled. Settings are saved, but Needlepath will not observe or alter runtime traffic."
    : needlepath.mode === "shadow"
      ? "Shadow mode. Telemetry is recorded while prompts and outputs stay unchanged."
      : needlepath.mode === "canary"
        ? `Canary mode. Optimization is active for ${Number(needlepath.canary_percentage) || 0}% of eligible runs.`
        : needlepath.mode === "on"
          ? "On. Optimization is active for all eligible runtime paths."
          : "Paused. Needlepath is enabled, but rollout is off.";

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.2,
        borderRadius: UI_RADIUS.panel,
        bgcolor: (theme) => needlepath.enabled
          ? (theme.palette.mode === "dark" ? "rgba(47,111,146,0.18)" : colors.blueSoft)
          : (theme.palette.mode === "dark" ? BRAND_COLORS.primary.softSubtle : "#FFF8ED"),
        color: (theme) => needlepath.enabled
          ? (theme.palette.mode === "dark" ? "#A7D8EF" : "#23526E")
          : (theme.palette.mode === "dark" ? "#F2C69B" : "#7A4D18"),
        border: "1px solid",
        borderColor: (theme) => needlepath.enabled
          ? (theme.palette.mode === "dark" ? "rgba(167,216,239,0.28)" : "#D1EAF4")
          : (theme.palette.mode === "dark" ? BRAND_COLORS.primary.border : "#F0DFC5"),
        fontWeight: 750,
      }}
    >
      {message}
    </Box>
  );
}

function NeedlepathField({ children }) {
  return (
    <Box
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: UI_RADIUS.input,
          bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#F7F2ED",
          "& fieldset": { borderColor: "transparent" },
          "&:hover fieldset": { borderColor: "#D8C9BB" },
          "&.Mui-focused fieldset": { borderColor: NEEDLEPATH_COLORS.copper },
        },
        "& .MuiFormHelperText-root": { mx: 0.5, color: "text.secondary" },
      }}
    >
      {children}
    </Box>
  );
}

function HelpedLabel({ label, help }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
      {label}
      <NeedlepathHelp help={help} />
    </Box>
  );
}

function normalizeNeedlepathConfig(config) {
  return { ...DEFAULT_NEEDLEPATH_CONFIG, ...(config || {}) };
}

export default function ConfigPage() {
  const navigate = useNavigate();
  const [hostPath, setHostPath] = useState("");
  const [savedHostPath, setSavedHostPath] = useState("");
  const [needlepath, setNeedlepath] = useState(DEFAULT_NEEDLEPATH_CONFIG);
  const [savedNeedlepath, setSavedNeedlepath] = useState(DEFAULT_NEEDLEPATH_CONFIG);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const { flags, setFlag } = useFeatureFlags();
  const { can } = useUser();
  const canManageFeatureFlags = Boolean(can?.(PERMISSIONS.CONFIG_FEATURE_FLAGS_MANAGE));
  
  // Local state for pending feature flag changes
  const [pendingFlags, setPendingFlags] = useState(flags);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    // Fetch current config
    getConfig().then(cfg => {
      setHostPath(cfg.host_path || "");
      setSavedHostPath(cfg.host_path || "");
      setPendingFlags(prev => ({
        ...prev,
        enableCreateTool: cfg.enable_create_tool !== false,
      }));
      const needlepathConfig = normalizeNeedlepathConfig(cfg.needlepath);
      setNeedlepath(needlepathConfig);
      setSavedNeedlepath(needlepathConfig);
      setFlag("enableCreateTool", cfg.enable_create_tool !== false);
    });
  }, [setFlag]);

  // Sync pendingFlags when flags change (e.g., on initial load from localStorage)
  useEffect(() => {
    setPendingFlags(flags);
  }, [flags]);

  useEffect(() => {
    // Check if there are unsaved changes
    const flagKeys = Object.keys(flags);
    const flagsChanged = flagKeys.some(key => pendingFlags[key] !== flags[key]);
    const hostPathChanged = hostPath !== savedHostPath;
    const needlepathChanged = JSON.stringify(needlepath) !== JSON.stringify(savedNeedlepath);
    setHasUnsavedChanges(flagsChanged || hostPathChanged || needlepathChanged);
  }, [pendingFlags, flags, hostPath, savedHostPath, needlepath, savedNeedlepath]);

  const handleFlagChange = (key, value) => {
    setPendingFlags(prev => ({ ...prev, [key]: value }));
  };

  const handleNeedlepathChange = (key, value) => {
    setNeedlepath(prev => ({ ...prev, [key]: value }));
  };

  const needlepathModeLabel = needlepath.enabled
    ? needlepath.mode === "shadow"
      ? "Enabled: shadow"
      : needlepath.mode === "canary"
        ? `Enabled: canary ${Number(needlepath.canary_percentage) || 0}%`
        : needlepath.mode === "on"
          ? "Enabled: on"
          : "Enabled: rollout off"
    : "Disabled";

  const handleSave = async () => {
    setLoading(true);
    setMsg("");
    
    try {
      let saveSuccess = true;
      const configUpdates = {};
      
      if (hostPath !== savedHostPath) {
        configUpdates.host_path = hostPath;
      }

      if (pendingFlags.enableCreateTool !== flags.enableCreateTool) {
        configUpdates.enable_create_tool = Boolean(pendingFlags.enableCreateTool);
      }

      if (JSON.stringify(needlepath) !== JSON.stringify(savedNeedlepath)) {
        configUpdates.needlepath = {
          ...needlepath,
          canary_percentage: Number(needlepath.canary_percentage) || 0,
          max_selected_tokens: Number(needlepath.max_selected_tokens) || 8000,
          max_selected_records: Number(needlepath.max_selected_records) || 40,
          timeout_ms: Number(needlepath.timeout_ms) || 500,
        };
      }

      // Save server-backed config only if it changed
      if (Object.keys(configUpdates).length > 0) {
        const data = await saveConfig(configUpdates);
        if (data.status === "success") {
          if (Object.prototype.hasOwnProperty.call(configUpdates, "host_path")) {
            setSavedHostPath(hostPath);
          }
          if (Object.prototype.hasOwnProperty.call(configUpdates, "needlepath")) {
            const saved = normalizeNeedlepathConfig(data.config?.needlepath || configUpdates.needlepath);
            setNeedlepath(saved);
            setSavedNeedlepath(saved);
          }
        } else {
          saveSuccess = false;
          setMsg("Failed to save configuration.");
        }
      }
      
      // Apply feature flag changes
      const flagKeys = Object.keys(flags);
      const hasFlgChanges = flagKeys.some(key => pendingFlags[key] !== flags[key]);
      
      if (hasFlgChanges) {
        flagKeys.forEach(key => {
          if (pendingFlags[key] !== flags[key]) {
            setFlag(key, pendingFlags[key]);
          }
        });
      }
      
      if (saveSuccess) {
        setMsg("Configuration saved successfully!");
      }
    } catch (error) {
      console.error("Save error:", error);
      setMsg("Failed to save configuration.");
    }
    
    setLoading(false);
  };

  const featureFlagRows = [
    {
      key: "newAgentPage",
      title: "New UI",
      description: "Enable the redesigned user interface.",
      checked: Boolean(pendingFlags["newAgentPage"]),
      onChange: (event) => handleFlagChange("newAgentPage", event.target.checked),
    },
    {
      key: "enablePolling",
      title: "Auto refresh",
      description: "Automatically refresh agent runs and workflow status.",
      checked: Boolean(pendingFlags["enablePolling"]),
      onChange: (event) => handleFlagChange("enablePolling", event.target.checked),
    },
    ...(canManageFeatureFlags
      ? [
          {
            key: "enableWorkflows",
            title: "Workflows",
            description: "Show workflow features in navigation.",
            checked: Boolean(pendingFlags["enableWorkflows"]),
            onChange: (event) => handleFlagChange("enableWorkflows", event.target.checked),
          },
          {
            key: "enableCreateTool",
            title: "Tool Forge",
            description: "Show Tool Forge and allow tool creation/import endpoints.",
            checked: pendingFlags["enableCreateTool"] !== false,
            onChange: (event) => handleFlagChange("enableCreateTool", event.target.checked),
          },
          {
            key: "whiteLabelDemo",
            title: "White-label demo",
            description: "Use a partner branding preset without changing saved tenant branding.",
            checked: Boolean(pendingFlags["whiteLabelDemo"]),
            onChange: (event) => handleFlagChange("whiteLabelDemo", event.target.checked),
          },
        ]
      : []),
  ];

  return (
    <Box sx={{ minHeight: "100%", bgcolor: "background.default", px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY }}>
      <Box sx={{ maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
        <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
          <Box>
            <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>
              Configuration
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75, maxWidth: 760 }}>
              Manage workspace features, optimization rollout, and runtime paths from one place.
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
            useFlexGap
            sx={{
              flexWrap: "wrap",
              "& .MuiButton-root": {
                width: { xs: "100%", sm: "auto" },
              },
            }}
          >
            {canManageFeatureFlags && <ConfigSectionToggle activeSection="features" />}
            {hasUnsavedChanges && !loading && (
              <Chip
                size="small"
                label="Unsaved changes"
                sx={{ alignSelf: { xs: "flex-start", sm: "center" }, bgcolor: "#FFF4D9", color: "#7A5B18", fontWeight: 850 }}
              />
            )}
            <Button
              variant="contained"
              disableElevation
              onClick={handleSave}
              disabled={loading || !hasUnsavedChanges}
              startIcon={loading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <SaveOutlinedIcon />}
              sx={CONFIG_ACTION_BUTTON_SX}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Stack>

        {msg && <Alert severity={msg.includes("saved") ? "success" : "error"} sx={{ mb: 2 }}>{msg}</Alert>}

        <Stack spacing={PAGE_SHELL.sectionGap}>
          <SettingsSection
            title="Feature flags"
            description="Control which major surfaces are visible in this workspace."
          >
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, columnGap: 3, rowGap: 0 }}>
              {featureFlagRows.map((item) => (
                <SettingRow
                  key={item.key}
                  title={item.title}
                  description={item.description}
                  checked={item.checked}
                  onChange={item.onChange}
                />
              ))}
            </Box>
          </SettingsSection>

      {canManageFeatureFlags && (
        <SettingsSection
          title="Needlepath"
          description="Choose how the token optimizer rolls out, what it may optimize, and how cautiously it behaves."
          action={
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Chip
                size="small"
                label={needlepathModeLabel}
                sx={{
                  bgcolor: needlepath.enabled ? "rgba(184,115,51,0.14)" : "#E8E4DF",
                  color: needlepath.enabled ? NEEDLEPATH_COLORS.copperDark : NEEDLEPATH_COLORS.muted,
                  fontWeight: 850,
                }}
              />
              <Chip
                size="small"
                label={`Safety: ${needlepath.safety_profile}`}
                sx={{ bgcolor: "action.hover", color: "text.primary", fontWeight: 750 }}
              />
              <Button
                variant="contained"
                disableElevation
                onClick={() => navigate("/needlepath")}
                startIcon={<AutoGraphIcon />}
                sx={CONFIG_ACTION_BUTTON_SX}
              >
                Live Dashboard
              </Button>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 850 }}>
                  Enabled
                </Typography>
                <Switch
                  checked={Boolean(needlepath.enabled)}
                  onChange={(e) => handleNeedlepathChange("enabled", e.target.checked)}
                />
                <NeedlepathHelp help={NEEDLEPATH_HELP.enabled} />
              </Box>
            </Stack>
          }
        >
          <Stack spacing={2}>
            <NeedlepathStatus needlepath={needlepath} />
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography sx={{ color: "text.primary", fontWeight: 900, fontSize: 18 }}>Rollout</Typography>
                <NeedlepathHelp help={NEEDLEPATH_HELP.mode} />
              </Stack>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1.25 }}>
                {ROLLOUT_OPTIONS.map((option) => (
                  <RolloutOption
                    key={option.value}
                    option={option}
                    active={needlepath.mode === option.value}
                    onSelect={(value) => handleNeedlepathChange("mode", value)}
                  />
                ))}
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "0.85fr 1.15fr" }, gap: 2 }}>
              <Box>
                <Typography sx={{ color: "text.primary", fontWeight: 900, fontSize: 18, mb: 1 }}>
                  Guardrail
                </Typography>
                <NeedlepathField>
                  <TextField
                    select
                    fullWidth
                    label={<HelpedLabel label="Safety profile" help={NEEDLEPATH_HELP.safety_profile} />}
                    value={needlepath.safety_profile}
                    onChange={(e) => handleNeedlepathChange("safety_profile", e.target.value)}
                    helperText={NEEDLEPATH_HELP.safety_profile.helper}
                  >
                    <MenuItem value="conservative">Conservative</MenuItem>
                    <MenuItem value="balanced">Balanced</MenuItem>
                    <MenuItem value="aggressive">Aggressive</MenuItem>
                  </TextField>
                </NeedlepathField>
              </Box>
              <Box>
                <Typography sx={{ color: "text.primary", fontWeight: 900, fontSize: 18, mb: 1 }}>
                  Scope
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" }, gap: 1.25 }}>
                  {[
                    ["input_optimization_enabled", "Input optimization"],
                    ["output_contract_enabled", "Output contracts"],
                    ["metrics_enabled", "Telemetry"],
                  ].map(([key, label]) => (
                    <ScopeToggle
                      key={key}
                      title={label}
                      helper={NEEDLEPATH_HELP[key].helper}
                      help={NEEDLEPATH_HELP[key]}
                      checked={Boolean(needlepath[key])}
                      onChange={(e) => handleNeedlepathChange(key, e.target.checked)}
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            {needlepath.mode === "canary" && (
              <Box sx={{ maxWidth: 420 }}>
                <NeedlepathField>
                  <TextField
                    type="number"
                    fullWidth
                    label={<HelpedLabel label="Canary percentage" help={NEEDLEPATH_HELP.canary_percentage} />}
                    value={needlepath.canary_percentage}
                    onChange={(e) => handleNeedlepathChange("canary_percentage", e.target.value)}
                    helperText={NEEDLEPATH_HELP.canary_percentage.helper}
                    inputProps={{ min: 0, max: 100 }}
                  />
                </NeedlepathField>
              </Box>
            )}

            <Box
              component="details"
              sx={{
                borderTop: 1,
                borderColor: "divider",
                pt: 2,
                "& summary": { listStyle: "none" },
                "& summary::-webkit-details-marker": { display: "none" },
              }}
            >
              <Box
                component="summary"
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  color: "text.primary",
                  fontWeight: 900,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: 18 }}>Advanced budgets and formats</Typography>
                  <Chip size="small" label="Advanced" sx={{ bgcolor: NEEDLEPATH_COLORS.soft, color: NEEDLEPATH_COLORS.copperDark, fontWeight: 850 }} />
                </Stack>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Keep defaults unless rollout review suggests tighter or looser behavior.
                </Typography>
              </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 2, mt: 2 }}>
                  <NeedlepathField>
                    <TextField
                      select
                      fullWidth
                      label={<HelpedLabel label="Context format" help={NEEDLEPATH_HELP.context_format} />}
                      value={needlepath.context_format}
                      onChange={(e) => handleNeedlepathChange("context_format", e.target.value)}
                      helperText={NEEDLEPATH_HELP.context_format.helper}
                    >
                      <MenuItem value="hybrid">Hybrid</MenuItem>
                      <MenuItem value="plain">Plain</MenuItem>
                      <MenuItem value="toon">TOON</MenuItem>
                    </TextField>
                  </NeedlepathField>
                  <NeedlepathField>
                    <TextField
                      select
                      fullWidth
                      label={<HelpedLabel label="Output mode" help={NEEDLEPATH_HELP.output_mode} />}
                      value={needlepath.output_mode}
                      onChange={(e) => handleNeedlepathChange("output_mode", e.target.value)}
                      helperText={NEEDLEPATH_HELP.output_mode.helper}
                    >
                      <MenuItem value="auto">Auto</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="concise">Concise</MenuItem>
                      <MenuItem value="dense">Dense</MenuItem>
                      <MenuItem value="ultra">Ultra</MenuItem>
                      <MenuItem value="off">Off</MenuItem>
                    </TextField>
                  </NeedlepathField>
                  <NeedlepathField>
                    <TextField
                      type="number"
                      fullWidth
                      label={<HelpedLabel label="Timeout ms" help={NEEDLEPATH_HELP.timeout_ms} />}
                      value={needlepath.timeout_ms}
                      onChange={(e) => handleNeedlepathChange("timeout_ms", e.target.value)}
                      helperText={NEEDLEPATH_HELP.timeout_ms.helper}
                      inputProps={{ min: 100, max: 5000 }}
                    />
                  </NeedlepathField>
                  <NeedlepathField>
                    <TextField
                      type="number"
                      fullWidth
                      label={<HelpedLabel label="Max selected tokens" help={NEEDLEPATH_HELP.max_selected_tokens} />}
                      value={needlepath.max_selected_tokens}
                      onChange={(e) => handleNeedlepathChange("max_selected_tokens", e.target.value)}
                      helperText={NEEDLEPATH_HELP.max_selected_tokens.helper}
                      inputProps={{ min: 1000, max: 64000 }}
                    />
                  </NeedlepathField>
                  <NeedlepathField>
                    <TextField
                      type="number"
                      fullWidth
                      label={<HelpedLabel label="Max selected records" help={NEEDLEPATH_HELP.max_selected_records} />}
                      value={needlepath.max_selected_records}
                      onChange={(e) => handleNeedlepathChange("max_selected_records", e.target.value)}
                      helperText={NEEDLEPATH_HELP.max_selected_records.helper}
                      inputProps={{ min: 1, max: 200 }}
                    />
                  </NeedlepathField>
                </Box>
            </Box>
          </Stack>
        </SettingsSection>
      )}

          <SettingsSection
            title="Runtime paths"
            description="Shared filesystem paths used by agent execution."
          >
            <NeedlepathField>
              <TextField
                label="Agent host path"
                value={hostPath}
                onChange={e => setHostPath(e.target.value)}
                fullWidth
                helperText="Mounted into agent Docker containers as a shared volume."
              />
            </NeedlepathField>
            {savedHostPath && (
              <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 1 }}>
                Current path: <b>{savedHostPath}</b>
              </Typography>
            )}
          </SettingsSection>
        </Stack>
      </Box>
    </Box>
  );
}
